// /webinar — Webinar funnel landing page (separate from the main /  landing).
//
// Standalone single-purpose page: one goal — register for the live webinar.
// Mirrors the classic webinar-funnel section order:
//   Hero (date + countdown + CTA)
//   → Opportunity ("Warum du LIVE dabei sein solltest")
//   → Target audience ("Ist dieses Webinar für dich?")
//   → Value reasons ("Das darfst du NICHT verpassen")
//   → About / credibility (Geschichte · Warum jetzt · Versprechen)
//   → Requirements (3 things you need)
//   → Final CTA
//   → Minimal footer
//
// Tracking + consent uses the same patterns as the main App.jsx — the CTA
// click is treated as click-wrap consent for Functional/Statistics/Marketing
// and opens the Calendly popup that doubles as the registration form.
//
// WEBINAR_DATE and WEBINAR_REGISTRATION_URL are the two knobs you tweak
// between webinar runs.

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { AAConsent } from './CookieBanner.jsx';
import { CookieBanner } from './CookieBanner.jsx';
import { trackCTAClick, trackScheduleIntent } from '../lib/tracking.js';
import { WEBINAR_DATE, WEBINAR_FORMAT, DATE_FMT, TIME_FMT } from '../lib/webinar.js';
import { attachLeadSourceFiller } from '../lib/lead-source.js';

// ActiveCampaign embed — form id 7 on the Incibit AC account. The placeholder
// div (`._form_7`) sits inside <WebinarFormModal/>; the script's auto-scan
// renders the form into it on load.
const AC_FORM_SCRIPT_SRC = 'https://incibit62741.activehosted.com/f/embed.php?id=7';
const AC_FORM_DIV_CLASS = '_form_7';

// AC hidden field that receives the captured lead source (utm_source / li /
// full query string). Form 7 declares this field via `window.cfields = {"2":"leadsource"}`,
// so the hidden input's name is `field[2]`.
const AC_LEAD_SOURCE_FIELD = 'field[2]';

// ─── CTA HANDLER ───────────────────────────────────────────────────────────
// CTAs across the page dispatch this event; the page-level WebinarFormModal
// listens for it and flips its `open` state. We avoid prop-drilling a setter
// through every section component this way.
const OPEN_EVENT = 'aa:open-webinar-modal';

function openWebinarRegistration(location) {
  // Click-wrap consent — opening the registration form implies opt-in for
  // Functional (AC + form-tracking cookies), Statistics (GA4) and Marketing
  // (Meta / Google Ads). Set BEFORE the track*() calls so the pixel doesn't
  // drop them at its consent gate.
  const c = AAConsent.current();
  if (!c.functional || !c.statistics || !c.marketing) {
    AAConsent.set({ functional: true, statistics: true, marketing: true });
  }
  trackCTAClick({ label: 'Webinar-Anmeldung', location, destination: 'activecampaign' });
  trackScheduleIntent({ location });
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { location } }));
}

function useCountdown(target) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, isPast: diff === 0 };
}

// ─── HEAD ──────────────────────────────────────────────────────────────────
// Override title + description for the webinar route. The main index.html
// metas are for /, so we set them imperatively when this page mounts.
function useWebinarHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Kostenloses Webinar der AI App Academy. In 6 klaren Schritten zur eigenen App.';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        'Kostenloses Live-Webinar der AI App Academy. Wie du mit KI in 6 klaren Schritten deine eigene App baust und im Store launchst. Ohne Programmierkenntnisse. Ohne teure Agentur. Jetzt Platz sichern.'
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute('content', prevDesc);
    };
  }, []);
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function WebinarNav() {
  return (
    <nav className="nav nav-webinar">
      <div className="nav-inner">
        <a href="#top" className="logo">
          <div className="logo-mark"><LogoMark size={32} /></div>
          <span className="logo-text">AI App Academy</span>
        </a>
        <a
          href="#anmeldung"
          onClick={(e) => { e.preventDefault(); openWebinarRegistration('nav'); }}
          className="btn btn-primary btn-sm nav-cta"
        >
          <span className="cta-label-full">Jetzt kostenlos anmelden</span>
          <span className="cta-label-short">Anmelden</span>
          <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
        </a>
      </div>
    </nav>
  );
}

function CountdownBox({ value, label }) {
  return (
    <div className="countdown-box">
      <div className="countdown-num">{String(value).padStart(2, '0')}</div>
      <div className="countdown-label">{label}</div>
    </div>
  );
}

function WebinarHero() {
  const { days, hours, minutes, seconds, isPast } = useCountdown(WEBINAR_DATE);

  return (
    <section className="hero webinar-hero" id="top">
      <div className="hero-bg-grid" aria-hidden="true"></div>
      <div className="container">
        <div className="webinar-eyebrow">
          <span className="webinar-live-dot"></span>
          {/* Already uppercase + capital ẞ so text-transform doesn't downgrade
              the name. The host suffix hides on mobile to keep the pill on one
              line; the name still shows up in subline + about, so no loss. */}
          <span style={{ textTransform: 'none' }}>
            KOSTENLOSES LIVE-WEBINAR<span className="webinar-eyebrow-host"> MIT DOMINIK GROẞ</span>
          </span>
        </div>

        <h1 className="hero-headline webinar-headline">
          Wie du mit <span className="accent">KI</span> <em>in 6 klaren Schritten</em> deine eigene App veröffentlichst,<br />
          ohne programmieren zu lernen und ohne Entwicklerteam.
        </h1>

        <p className="webinar-headline-counter">
          statt 50.000–100.000 € klassisches Agenturbudget zu brauchen
        </p>

        <p className="webinar-sub">
          Im kostenlosen Live-Webinar zeigt dir <strong>Dominik Groß</strong>, wie du mit
          <strong> Cursor und KI</strong> in 6 klaren Schritten aus deiner Idee eine eigene App im App
          Store machst. Ohne Agentur, ohne Technik-Chaos und ohne monatelange Entwicklungszeit.
        </p>

        <div className="event-meta-pill">
          <span className="event-meta-item">
            <Icon.Calendar size={16} />
            <span>{DATE_FMT.format(WEBINAR_DATE)}</span>
          </span>
          <span className="event-meta-divider"></span>
          <span className="event-meta-item">
            <Icon.Clock size={16} />
            <span>{TIME_FMT.format(WEBINAR_DATE)} Uhr</span>
          </span>
          <span className="event-meta-divider"></span>
          <span className="event-meta-item">
            <span>{WEBINAR_FORMAT}</span>
          </span>
        </div>

        {!isPast && (
          <div className="countdown" aria-label="Countdown bis zum Webinar">
            <CountdownBox value={days} label="Tage" />
            <span className="countdown-sep">:</span>
            <CountdownBox value={hours} label="Stunden" />
            <span className="countdown-sep">:</span>
            <CountdownBox value={minutes} label="Minuten" />
            <span className="countdown-sep">:</span>
            <CountdownBox value={seconds} label="Sekunden" />
          </div>
        )}

        <div className="hero-cta-row webinar-cta-row">
          <a
            href="#anmeldung"
            onClick={(e) => { e.preventDefault(); openWebinarRegistration('hero'); }}
            className="btn btn-primary btn-xl"
          >
            <span className="btn-label-stack">
              <span className="btn-main">Jetzt kostenlosen Platz sichern</span>
              <span className="btn-sub">100 % kostenlos · Live online · kein Verkaufsgespräch</span>
            </span>
            <span className="btn-arrow"><Icon.ArrowRight size={18} /></span>
          </a>
        </div>

        <div className="hero-microproof">
          <span className="hero-microproof-item"><Icon.Shield size={14} /> 15+ Jahre App-Studio-Erfahrung</span>
          <span className="hero-microproof-dot"></span>
          <span className="hero-microproof-item"><Icon.CheckCircle size={14} /> 30+ Apps live im Store</span>
          <span className="hero-microproof-dot"></span>
          <span className="hero-microproof-item"><Icon.Star size={14} /> Auch ohne Programmierkenntnisse</span>
        </div>
      </div>
    </section>
  );
}

function WebinarOpportunity() {
  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Warum jetzt</span>
          <h2 className="section-title">Warum eine eigene App heute zum ersten Mal <span className="serif-italic">realistisch umsetzbar</span> ist.</h2>
          <p className="lead">
            Was früher Entwicklerteam, 50.000–100.000 € Agenturbudget und Monate an Projektzeit
            gebraucht hat, lässt sich heute mit KI in 6 klaren Schritten umsetzen. Genau das macht eine
            eigene App plötzlich auch für Coaches, Experten, Gründer und Unternehmer realistisch.
          </p>
          <p className="lead">
            Wer jetzt versteht, mit KI eigene Produkte zu bauen, baut sich einen Zukunfts-Skill
            auf, der in den nächsten Jahren massiv an Wert gewinnt. Was heute ein echter Vorsprung
            ist, wird in 12 bis 24 Monaten Standard. Wer jetzt anfängt, etabliert sich im eigenen
            Markt. Wer wartet, kommt in einen Wettbewerb, in dem andere bereits Platz genommen haben.
          </p>
        </div>
      </div>
    </section>
  );
}

function WebinarAudience() {
  const personas = [
    {
      tag: 'Coaches & Experten',
      headline: 'Dein Wissen wird zum digitalen Produkt.',
      bullets: [
        'Du willst dein Wissen in ein digitales Produkt mit höherer Bindung und besserer Skalierbarkeit überführen',
        'Du willst wiederkehrenden Umsatz aufbauen, statt Zeit gegen Geld zu tauschen',
      ],
    },
    {
      tag: 'Unternehmer & KMU',
      headline: 'Ein eigener digitaler Kanal in deiner Hand.',
      bullets: [
        'Du willst einen eigenen digitalen Kanal aufbauen, statt von Plattformen oder Dienstleistern abhängig zu sein',
        'Du siehst in einer App einen Hebel für Kundenbindung, Monetarisierung oder Differenzierung',
      ],
    },
    {
      tag: 'Gründer mit App-Idee',
      headline: 'Deine Idee endlich raus aus dem Notizbuch.',
      bullets: [
        'Du hast eine konkrete App-Idee, aber keinen technischen Background',
        'Du suchst einen realistischen Weg ohne 50.000 €+ Agenturbudget',
      ],
    },
  ];
  return (
    <section className="section forwho-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Für wen es gemacht ist</span>
          <h2 className="section-title">Ist dieses Webinar <span className="serif-italic">für dich</span>?</h2>
        </div>
        <div className="webinar-audience-grid">
          {personas.map((p, i) => (
            <div className="webinar-audience-card" key={i}>
              <div className="webinar-audience-tag">{p.tag}</div>
              <h3 className="webinar-audience-headline">{p.headline}</h3>
              <ul className="webinar-audience-bullets">
                {p.bullets.map((b, j) => (
                  <li key={j}><Icon.Check size={14} /> <span>{b}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WebinarDisqualification() {
  const bullets = [
    'du nur mal ein bisschen mit KI herumspielen willst',
    'du aktuell weder ein konkretes Problem noch eine echte Anwendung für eine App siehst',
    'du nach dem schnellsten kostenlosen Hack suchst, statt ein echtes Produkt aufzubauen',
    'du keine Bereitschaft hast, Zeit, Fokus und später auch Investment in die Umsetzung zu stecken',
  ];
  return (
    <section className="section webinar-disqualification-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Ehrlichkeit zuerst</span>
          <h2 className="section-title">Nicht das Richtige für dich, <span className="serif-italic">wenn</span> …</h2>
        </div>
        <div className="webinar-disqualification-box">
          <ul className="webinar-disqualification-list">
            {bullets.map((b, i) => (
              <li key={i}><Icon.X size={16} /> <span>{b}</span></li>
            ))}
          </ul>
          <p className="webinar-disqualification-footnote">
            Diese Klarheit hilft uns beiden. Wer sich hier wiederfindet, holt aus dem Webinar nichts raus.
            Wer sich in den drei Profilen oben wiederfindet, sehr viel.
          </p>
        </div>
      </div>
    </section>
  );
}

// Slim mid-page CTA banner. Repeats the registration prompt with the event
// date next to the button so visitors reading deep into the page don't have
// to scroll back up or jump to the top-right sticky CTA to convert.
function InlineCTABanner({ location, headline = 'Bereit für deine eigene App?' }) {
  return (
    <section className="inline-cta-banner">
      <div className="container">
        <div className="inline-cta-card">
          <div className="inline-cta-text">
            <div className="inline-cta-headline">{headline}</div>
            <div className="inline-cta-meta">
              <Icon.Calendar size={14} />
              <span>{DATE_FMT.format(WEBINAR_DATE)} · {TIME_FMT.format(WEBINAR_DATE)} Uhr · {WEBINAR_FORMAT}</span>
            </div>
          </div>
          <a
            href="#anmeldung"
            onClick={(e) => { e.preventDefault(); openWebinarRegistration(location); }}
            className="btn btn-primary inline-cta-btn"
          >
            <span>Jetzt kostenlosen Platz sichern</span>
            <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
          </a>
        </div>
      </div>
    </section>
  );
}

// Visual proof break — one of the apps the team has built, presented as a
// device mockup so the "30+ Apps live im Store" claim becomes concrete.
function WebinarAppShowcase() {
  return (
    <section className="section webinar-showcase-section">
      <div className="container">
        <div className="webinar-showcase-grid">
          <div className="webinar-showcase-text">
            <span className="eyebrow">Echte Apps. Echte Stores.</span>
            <h2 className="webinar-showcase-title">
              30+ Apps live im Store. <span className="serif-italic">Gebaut als Geschäftsmodelle</span>, nicht als Tech-Demos.
            </h2>
            <p>
              Von Coaching-Tools über Lern-Apps und Community-Plattformen bis zu Mitgliederbereichen
              und Branded-Apps für Unternehmen. Entscheidend ist nicht, dass eine App technisch möglich
              ist. Entscheidend ist, dass sie geschäftlich Sinn macht. Genau darauf ist dieses Webinar
              ausgerichtet.
            </p>
            <ul className="webinar-showcase-stats">
              <li>
                <span className="webinar-showcase-stat-num">30+</span>
                <span className="webinar-showcase-stat-lbl">Apps im Store</span>
              </li>
              <li>
                <span className="webinar-showcase-stat-num">1 Mio.+</span>
                <span className="webinar-showcase-stat-lbl">Downloads weltweit</span>
              </li>
              <li>
                <span className="webinar-showcase-stat-num">15+</span>
                <span className="webinar-showcase-stat-lbl">Jahre App-Studio</span>
              </li>
            </ul>
          </div>
          <div className="webinar-showcase-visual">
            <img
              src="/assets/lernapp%20mockups_klein.png"
              alt="Beispiel-App auf drei Smartphones: Lerneinheiten, Fortschritt und Profil"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function WebinarObjections() {
  const items = [
    {
      objection: 'Ich bin nicht technisch genug.',
      response: 'Du musst kein Entwickler sein. Entscheidend ist nicht, ob du Code schreiben kannst, sondern ob du ein klares Problem, einen sinnvollen Use Case und den richtigen Umsetzungsprozess hast.',
    },
    {
      objection: 'Brauche ich ein Entwicklerteam?',
      response: 'Nein. Genau das ist der entscheidende Unterschied. Mit den richtigen KI-Tools und einem klaren Prozess setzt du deine App selbst um, ohne Developer einstellen, schulen oder steuern zu müssen.',
    },
    {
      objection: 'Ist das nicht extrem teuer?',
      response: 'Im Vergleich zum klassischen Agenturweg, der schnell sechsstellig wird, bewegt sich der Aufwand hier in einer komplett anderen Größenordnung. Im Webinar gehen wir konkret auf die Kostenfrage ein, ohne Pitch.',
    },
    {
      objection: 'Wofür kann eine App in meinem Business überhaupt sinnvoll sein?',
      response: 'Du lernst, wann eine App ein echter Hebel für Kundenbindung, Monetarisierung und Skalierung ist. Und wann nicht. Anhand konkreter Modelle aus der Praxis statt aus dem Lehrbuch.',
    },
  ];
  return (
    <section className="section webinar-objections-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Häufige Zweifel</span>
          <h2 className="section-title">Vielleicht denkst du <span className="serif-italic">gerade</span> …</h2>
        </div>
        <div className="webinar-objections-grid">
          {items.map((it, i) => (
            <div className="webinar-objection-card" key={i}>
              <div className="webinar-objection-quote">„{it.objection}"</div>
              <p className="webinar-objection-response">{it.response}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Subtle social-proof strip — three verbatim customer quotes from the
// Incibit Studio's client portfolio. Quotes are unchanged sentences from
// the main-site testimonials. Section is framed as "Stimmen aus dem Studio"
// so it's transparent that these are Incibit clients (where the team built
// the app), not Academy alumni — Trust-Signal über das Studio, das hinter
// der Academy steht.
function WebinarTestimonials() {
  const items = [
    {
      quote: 'Unsere hohen Designansprüche konnten zu unserer vollsten Zufriedenheit umgesetzt werden.',
      name: 'Armin Kienreich',
      role: 'Easypreneur',
      photo: '/assets/armin-kienreich.jpg',
    },
    {
      quote: 'Ich habe nun eine App, die genau meinen Vorstellungen entspricht und großen Mehrwert für Nutzer bietet.',
      name: 'Daniel Bruck',
      role: 'Uniprep',
      photo: '/assets/daniel-bruck.jpg',
    },
    {
      quote: 'Wer einen verlässlichen Partner mit hervorragender Ergebnisqualität sucht, ist bei Dominik und seinem Team bestens aufgehoben.',
      name: 'Milan Wolfs',
      role: 'Gardify',
      photo: '/assets/milan-wolfs.jpg',
    },
  ];
  return (
    <section className="section webinar-testimonials-section">
      <div className="container">
        <ul className="webinar-testimonials-row">
          {items.map((t, i) => (
            <li className="webinar-testimonial-item" key={i}>
              <p className="webinar-testimonial-quote">„{t.quote}"</p>
              <div className="webinar-testimonial-author">
                <div className="webinar-testimonial-photo">
                  <img src={t.photo} alt="" loading="lazy" decoding="async" width="44" height="44" />
                </div>
                <div className="webinar-testimonial-meta">
                  <span className="webinar-testimonial-name">{t.name}</span>
                  <span className="webinar-testimonial-role">{t.role}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function WebinarAbout() {
  return (
    <section className="section webinar-about-section">
      <div className="container">
        <div className="webinar-about-hero">
          <div className="webinar-about-photo">
            <img
              src="/assets/shooting_chris_joergler_dominik_small.jpg"
              alt="Dominik Groß, Gründer der AI App Academy"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="webinar-about-content">
            <span className="eyebrow">Wer dich begleitet</span>
            <h2 className="section-title">Praxis statt <span className="serif-italic">Theorie</span>.</h2>
            <p className="lead">
              Dominik Groß leitet seit über 15 Jahren das App-Studio Incibit. Mit seinem Team hat
              er <strong>Projekte zwischen 50.000 € und 200.000 €</strong> für Coaches, KMU, Marken
              und Investoren-finanzierte Startups umgesetzt. Über 30 Apps live, über 1 Mio.
              Downloads. Erfahrung aus realen Geschäftsmodellen, nicht aus dem Online-Kurs-Markt.
            </p>
            <ul className="webinar-about-bullets">
              <li><Icon.Check size={16} /> 15+ Jahre App-Entwicklung im eigenen Studio</li>
              <li><Icon.Check size={16} /> 30+ Apps live im Apple App Store und bei Google Play</li>
              <li><Icon.Check size={16} /> Über 1 Mio. Downloads weltweit</li>
              <li><Icon.Check size={16} /> Du bekommst keinen Technik-Vortrag, sondern einen praxisnahen Einblick, wie aus Expertise, Zielgruppe und Use Case ein umsetzbares App-Produkt wird</li>
            </ul>
          </div>
        </div>

        <div className="webinar-promise-block">
          <div className="webinar-about-eyebrow webinar-promise-eyebrow">Mein Versprechen</div>
          <p className="webinar-promise-text">
            Keine Motivationssätze, kein Verkaufspitch. Du bekommst einen praxisnahen Einblick,
            wie aus Wissen, Zielgruppe oder Use Case ein umsetzbares App-Produkt wird. Was
            wirklich nötig ist, wo die Fallstricke liegen und welche Schritte sich rechnen.
          </p>
          {/* Already uppercase with capital ẞ so the CSS text-transform:uppercase
              doesn't downgrade ß → SS in Dominik's last name. */}
          <div className="webinar-promise-signature" style={{ textTransform: 'none' }}>DOMINIK GROẞ</div>
        </div>
      </div>
    </section>
  );
}

function WebinarRequirements() {
  const items = [
    {
      num: '01',
      text: 'Du hast eine App-Idee, ein bestehendes Angebot oder ein konkretes Thema, das du in eine App überführen willst.',
    },
    {
      num: '02',
      text: 'Du willst verstehen, wie so ein Projekt heute realistisch umsetzbar wird.',
    },
    {
      num: '03',
      text: 'Du bist offen dafür, KI als Hebel statt klassischer Agenturentwicklung zu nutzen.',
    },
  ];
  return (
    <section className="section webinar-requirements-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Damit es für dich passt</span>
          <h2 className="section-title">Was dir hilft, damit das Webinar für dich <span className="serif-italic">besonders relevant</span> ist.</h2>
          <p className="lead">
            Du brauchst keine Programmierkenntnisse und kein Entwicklerteam. Was du wirklich
            mitbringen solltest, sind diese 3 Dinge:
          </p>
        </div>
        <div className="webinar-requirements-grid">
          {items.map((it, i) => (
            <div className="webinar-req-card" key={i}>
              <div className="webinar-req-num">{it.num}</div>
              <p>{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WebinarFinalCTA() {
  const { days, hours, minutes, seconds, isPast } = useCountdown(WEBINAR_DATE);

  return (
    <section className="cta-final webinar-final-cta" id="anmeldung">
      <div className="container">
        <div className="cta-final-content">
          <div className="webinar-final-logo">
            <LogoMark size={56} />
          </div>
          <span className="eyebrow" style={{ marginBottom: 24, display: 'inline-flex', justifyContent: 'center' }}>
            Dein nächster Schritt
          </span>
          <h2>
            Meld dich jetzt für das kostenlose <em className="serif-italic">Live-Webinar</em> an.
          </h2>
          <p>
            Plätze sind bewusst begrenzt, damit am Ende echte Q&amp;A zu deinem konkreten Vorhaben
            möglich ist. Sichere dir jetzt deinen Platz für {DATE_FMT.format(WEBINAR_DATE)} um
            {' '}{TIME_FMT.format(WEBINAR_DATE)} Uhr.
          </p>

          {!isPast && (
            <div className="countdown countdown-compact" aria-label="Countdown bis zum Webinar">
              <CountdownBox value={days} label="Tage" />
              <span className="countdown-sep">:</span>
              <CountdownBox value={hours} label="Stunden" />
              <span className="countdown-sep">:</span>
              <CountdownBox value={minutes} label="Minuten" />
              <span className="countdown-sep">:</span>
              <CountdownBox value={seconds} label="Sekunden" />
            </div>
          )}

          <a
            href="#"
            onClick={(e) => { e.preventDefault(); openWebinarRegistration('final'); }}
            className="btn btn-primary btn-xl"
          >
            <span className="btn-label-stack">
              <span className="btn-main">Jetzt kostenlosen Platz sichern</span>
              <span className="btn-sub">100 % kostenlos · Live online · kein Verkaufsgespräch</span>
            </span>
            <span className="btn-arrow"><Icon.ArrowRight size={18} /></span>
          </a>

          <div className="cta-meta">
            <span><Icon.CheckCircle size={14} /> 100 % kostenlos</span>
            <span><Icon.CheckCircle size={14} /> Live online</span>
            <span><Icon.CheckCircle size={14} /> Plätze sind begrenzt</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function WebinarFooter() {
  return (
    <footer className="webinar-footer">
      <div className="container">
        <div className="webinar-footer-row">
          <div className="webinar-footer-brand">
            <div className="logo-mark"><LogoMark size={24} /></div>
            <span>© 2026 Incibit GmbH · App Academy</span>
          </div>
          <div className="webinar-footer-links">
            <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
            <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
            <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
            <a
              href="#cookie-settings"
              onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
            >
              Cookie-Einstellungen
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── REGISTRATION MODAL ────────────────────────────────────────────────────
// ActiveCampaign embed wrapped in a Cookie-Banner-style dialog. The form
// placeholder div is mounted ONCE on first page render (not gated on `open`)
// and the AC embed script is injected straight after; that way the form is
// fully hydrated by the time the user clicks a CTA, so the modal feels
// instant on open. Subsequent opens just toggle visibility — the form keeps
// its internal state (any text the user typed) across close/reopen cycles.
function WebinarFormModal({ open, onClose }) {
  const scriptInjectedRef = React.useRef(false);
  const formContainerRef = React.useRef(null);

  // Inject the AC embed script exactly once. requestAnimationFrame guarantees
  // the placeholder div is committed to the real DOM before the script runs
  // — AC's auto-scan only finds divs that exist at script-execution time.
  React.useEffect(() => {
    if (scriptInjectedRef.current) return;
    scriptInjectedRef.current = true;
    requestAnimationFrame(() => {
      const s = document.createElement('script');
      s.src = AC_FORM_SCRIPT_SRC;
      s.async = true;
      s.charset = 'utf-8';
      document.body.appendChild(s);
    });
  }, []);

  // Watch the AC form container and fill the lead-source hidden field as soon
  // as the AC script renders the form. The observer self-disconnects on success
  // or after a 10-second safety timeout (see attachLeadSourceFiller).
  React.useEffect(() => {
    if (!formContainerRef.current) return;
    return attachLeadSourceFiller(formContainerRef.current, AC_LEAD_SOURCE_FIELD);
  }, []);

  // Lock body scroll + wire Escape while open.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <div
      className={`webinar-modal-backdrop ${open ? 'is-open' : ''}`}
      aria-hidden={!open}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="webinar-modal" role="dialog" aria-modal="true" aria-labelledby="webinar-modal-title">
        <button
          type="button"
          className="webinar-modal-close"
          onClick={onClose}
          aria-label="Schließen"
        >
          <Icon.X size={20} />
        </button>
        <div className="webinar-modal-header">
          <div className="webinar-modal-eyebrow">Kostenlose Webinar-Anmeldung</div>
          <h2 id="webinar-modal-title">Sichere dir jetzt deinen Platz</h2>
          <p className="webinar-modal-meta">
            {DATE_FMT.format(WEBINAR_DATE)} · {TIME_FMT.format(WEBINAR_DATE)} Uhr · {WEBINAR_FORMAT}
          </p>
        </div>
        {/* ActiveCampaign target div — the AC embed script renders the form here. */}
        <div ref={formContainerRef} className={AC_FORM_DIV_CLASS}></div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function WebinarPage2() {
  useWebinarHead();
  const [modalOpen, setModalOpen] = React.useState(false);

  // Bus: every CTA dispatches OPEN_EVENT, the page-level state flips here.
  React.useEffect(() => {
    const onOpen = () => setModalOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Reveal-on-scroll — mirrors the behaviour in App.jsx so sections fade in.
  React.useEffect(() => {
    const sections = document.querySelectorAll('section');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in'); });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    sections.forEach((s) => {
      s.classList.add('reveal-on-scroll');
      const rect = s.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) s.classList.add('in');
      io.observe(s);
    });
    const safety = setTimeout(() => {
      document.querySelectorAll('section.reveal-on-scroll:not(.in)').forEach((s) => {
        const rect = s.getBoundingClientRect();
        if (rect.top < window.innerHeight * 1.5) s.classList.add('in');
      });
    }, 1000);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);

  return (
    <>
      <WebinarNav />
      <WebinarHero />
      <WebinarOpportunity />
      <WebinarAudience />
      <InlineCTABanner location="inline-1" />
      <WebinarAppShowcase />
      <WebinarTestimonials />
      <WebinarAbout />
      <InlineCTABanner location="inline-2" headline="Klingt nach deinem nächsten Schritt?" />
      <WebinarObjections />
      <WebinarFinalCTA />
      <WebinarFooter />
      <WebinarFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <CookieBanner />
    </>
  );
}
